/**
 * Preloads the most-cited PDF documents into the in-memory cache so that
 * citation clicks open instantly instead of waiting for a network fetch.
 *
 * Uses `requestIdleCallback` so preloading yields to user interaction.
 */

import { useEffect, useRef } from 'react';
import { pdfCache } from '../services/pdfCacheService';
import type { MedicalEvent } from '../services/chronologyApi';

const DEFAULT_MAX_PRELOAD = 3;

/**
 * Count citation frequency per documentId across sourceReference and citations[].
 * Returns document IDs sorted by frequency descending.
 */
function rankDocumentsByCitationFrequency(events: MedicalEvent[]): string[] {
  const freq = new Map<string, number>();

  for (const event of events) {
    // sourceReference always present
    const srcDocId = event.sourceReference?.documentId;
    if (srcDocId) {
      freq.set(srcDocId, (freq.get(srcDocId) || 0) + 1);
    }

    // Additional citations array
    if (event.citations) {
      for (const cit of event.citations) {
        if (cit.documentId) {
          freq.set(cit.documentId, (freq.get(cit.documentId) || 0) + 1);
        }
      }
    }
  }

  return [...freq.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([docId]) => docId);
}

export function usePdfPreloader(
  events: MedicalEvent[] | undefined,
  options?: { maxPreload?: number },
): void {
  const maxPreload = options?.maxPreload ?? DEFAULT_MAX_PRELOAD;
  const preloadedRef = useRef(false);

  useEffect(() => {
    if (!events || events.length === 0 || preloadedRef.current) return;
    preloadedRef.current = true;

    const ranked = rankDocumentsByCitationFrequency(events);
    const toPreload = ranked
      .filter((docId) => !pdfCache.has(docId))
      .slice(0, maxPreload);

    for (const docId of toPreload) {
      pdfCache.preload(docId, true);
    }
  }, [events, maxPreload]);
}
